import { ReactNode } from 'react';
import { message } from '../../../../utils';
import { useFeatureFlag } from '../../../misc';
import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  Icon,
  IconName,
  LinkButton,
  LinkButtonProps,
  Paragraph
} from '../../atoms';

export type SecondaryButtonProps = {
  text: string;
  to: string;
  icon?: IconName;
};

export type MainButtonProps = {
  label: string;
  iconName: IconName;
  to?: string;
} & LinkButtonProps;

type CardContent = {
  mainButton: MainButtonProps;
  secondaryButton?: SecondaryButtonProps;
  readMoreButtonText?: string;
};

type CardTemplateProps = {
  image: string;
  name: string;
  cardContent: CardContent;
  onClick?: () => void;
  statusComponent: ReactNode;
  provider?: string | null;
  expiresOnComponent?: ReactNode;
  menu?: ReactNode;
};

export function CardTemplate({
  image,
  name,
  onClick,
  statusComponent,
  cardContent,
  provider,
  expiresOnComponent,
  menu
}: CardTemplateProps): JSX.Element {
  const secondaryButton = cardContent.secondaryButton;
  const hideReadMoreButtonFlag = useFeatureFlag(
    'hideReadMoreButtonBudgetAndBenefits'
  );

  const { iconName, label, ...mainButtonProps } = cardContent.mainButton;
  const MainButton = mainButtonProps.to ? LinkButton : Button;

  return (
    <Card
      boxShadow="none"
      border="1px solid"
      borderColor="gray.50"
      width={{ base: '100%', xl: '45%', '2xl': '45%' }}
      data-testid="card"
      title={name}
    >
      <CardBody>
        <Flex w="100%" gap={3} h="100%">
          <Box
            w="100px"
            h="100px"
            position="relative"
            borderRadius="10px"
            backgroundColor="palette_blue.0"
          >
            <Flex
              minW="100%"
              minH="100%"
              alignItems="center"
              justifyContent="center"
              padding="space_unit.x1"
            >
              <img src={image} alt={name} />
            </Flex>
          </Box>
          <Box flex={1} whiteSpace="normal" mr="space_unit.x1" flexGrow={5}>
            <Flex h="100%" flexDir="column" justifyContent="space-between">
              <Heading variant="h5">{name}</Heading>
              {provider && <Paragraph size="md">{provider}</Paragraph>}
              {onClick && !hideReadMoreButtonFlag ? (
                <Button
                  variant="tertiary"
                  onClick={onClick}
                  alignSelf="flex-start"
                >
                  <Paragraph fontSize="0.93rem" textDecor="underline">
                    {message(cardContent.readMoreButtonText, 'Read More')}
                  </Paragraph>
                </Button>
              ) : null}
            </Flex>
            {expiresOnComponent && expiresOnComponent}
          </Box>

          <Flex
            flexShrink={4}
            direction="column"
            justifyContent="space-between"
            alignItems="flex-end"
          >
            <Box w={'fit-content'} flex={1}>
              {statusComponent}
            </Box>
            <Flex>
              {secondaryButton ? (
                <LinkButton
                  mr="space_unit.x1"
                  to={secondaryButton.to}
                  leftIcon={
                    secondaryButton.icon ? (
                      <Icon w="16px" name={secondaryButton.icon} />
                    ) : undefined
                  }
                  variant="secondary"
                  data-testid="card-btn"
                >
                  {secondaryButton.text}
                </LinkButton>
              ) : null}

              <MainButton
                mr="space_unit.x1"
                leftIcon={<Icon w="16px" name={iconName} />}
                data-testid="card-btn"
                data-intercom-target="admin-navigationbar-company-budgets-detailsbutton"
                variant="primary"
                {...mainButtonProps}
              >
                {label}
              </MainButton>

              {menu ? menu : null}
            </Flex>
          </Flex>
        </Flex>
      </CardBody>
    </Card>
  );
}
